import { c as _c } from "react/compiler-runtime";
import { useMediaQueryList } from "@hooks/use-media-query";
import { LocalStorageUtils } from "@utils/local-storage-utils";
import { AnnotationSidePanelEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

/** How the user resized the side panel */

/** Width of the side panel without the viewer preview */
const DEFAULT_WIDTH = 430;

/** Narrowest the docked side panel can be resized to, still showing its details readably */
const MIN_DOCKED_WIDTH = 400;

/**
 * Width the docked side panel starts with: the narrowest, so it covers as little of the
 * annotations as possible. It can be widened to show the preview next to the details.
 */
export const DEFAULT_DOCKED_WIDTH = MIN_DOCKED_WIDTH;

/** Narrowest docked side panel that shows the preview and the details side by side */
const MIN_WIDE_WIDTH = 760;

/** Share of the window the docked side panel can take at most */
const MAX_DOCKED_WIDTH_RATIO = 0.8;

/** Separate from the previous layout's key, so the narrower widths stored then do not apply */
const WIDTH_STORAGE_KEY = "markups-side-panel-floating-width";
/** Layout of the annotation side panel, docked on every screen but the smallest */
export function useMarkupSidePanelLayout() {
  const $ = _c(10);
  const {
    isScreenXs
  } = useMediaQueryList();
  const [dockedWidth, setDockedWidth] = useState(getStoredWidth);
  const {
    trackEvent
  } = useTrackEvent();
  let t0;
  if ($[0] !== dockedWidth || $[1] !== trackEvent) {
    t0 = function persistWidth(method, t1) {
      const width = t1 === undefined ? dockedWidth : t1;
      const previousWidth = getStoredWidth();
      const newWidth = clampWidth(width);
      LocalStorageUtils.setStringItem(WIDTH_STORAGE_KEY, String(newWidth));
      if (newWidth !== previousWidth) {
        trackEvent({
          name: AnnotationSidePanelEvents.resizeSidePanel,
          props: {
            method,
            width: newWidth,
            previousWidth,
            isWide: newWidth >= MIN_WIDE_WIDTH
          }
        });
      }
    };
    $[0] = dockedWidth;
    $[1] = trackEvent;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const persistWidth = t0;
  const isDocked = !isScreenXs;
  const t1 = isDocked && dockedWidth >= MIN_WIDE_WIDTH;
  const t2 = isDocked ? dockedWidth : DEFAULT_WIDTH;
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getMaxWidth();
    t4 = width_0 => setDockedWidth(clampWidth(width_0));
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== isDocked || $[6] !== persistWidth || $[7] !== t1 || $[8] !== t2) {
    t5 = {
      isDocked,
      isWide: t1,
      width: t2,
      minWidth: MIN_DOCKED_WIDTH,
      maxWidth: t3,
      resize: t4,
      persistWidth
    };
    $[5] = isDocked;
    $[6] = persistWidth;
    $[7] = t1;
    $[8] = t2;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function getStoredWidth() {
  const stored = Number(LocalStorageUtils.getStringItem(WIDTH_STORAGE_KEY));
  return clampWidth(stored || DEFAULT_DOCKED_WIDTH);
}
function getMaxWidth() {
  return Math.round(Math.max(MIN_DOCKED_WIDTH, window.innerWidth * MAX_DOCKED_WIDTH_RATIO));
}
function clampWidth(width) {
  return Math.round(Math.min(Math.max(width, MIN_DOCKED_WIDTH), getMaxWidth()));
}